import { useEffect, useState } from "react";
import Button from "../../../Components/Button";
import { IDeltaHedgeExeCommonConfigDTO } from "../../../Interfaces/deltaHedgeExe";
import TextInput from "../../../Components/TextInput";
import {
  getDeltaHedgeExeCentralConfig,
  setDeltaHedgeExeCentralConfig,
} from "../../../Requests/deltaHedgeExe";
import { toast } from "react-toastify";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import FullScreenLoading from "../../../Components/Loading/FullScreenLoading";

interface IProps {
  onClose: () => void;
  formValues: IDeltaHedgeExeCommonConfigDTO;
  setFormValues: (values: IDeltaHedgeExeCommonConfigDTO) => void;
}

const CentralConfig = ({ onClose, formValues, setFormValues }: IProps) => {
  const [loading, setLoading] = useState(false);

  const commonChangeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const changeHandler = (name: string, value: any) => {
    const nameParts = name.split(".");
    setFormValues({
      ...formValues,
      [nameParts[0]]: {
        ...formValues[
          nameParts[0] as Exclude<
            keyof IDeltaHedgeExeCommonConfigDTO,
            "goalDeltaTolerance" | "goalGammaTolerance"
          >
        ],
        [nameParts[1]]: value,
      },
    });
  };

  const submitHandler = () => {
    setLoading(true);
    setDeltaHedgeExeCentralConfig(formValues)
      .then((resp) => {
        if (resp.data) {
          onClose();
          toast.success(resp.msg);
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const getForm = (
    title: string,
    fieldName: Exclude<
      keyof IDeltaHedgeExeCommonConfigDTO,
      "goalDeltaTolerance" | "goalGammaTolerance"
    >
  ) => (
    <div className="grid grid-cols-6 gap-x-2 gap-y-3 w-full border-b border-gray-400 pb-3">
      <div className="font-bold">{title}</div>
      <div />
      <div />
      <div />
      <div />
      <div />
      <TextInput
        label="حداقل بازده قفل روزانه (تقویت)"
        name={`${fieldName}.minDailyLockProfitReinforcement`}
        value={formValues[
          fieldName
        ]?.minDailyLockProfitReinforcement?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداقل بازده قفل روزانه (خنثی-همجنس-هم سررسید)"
        name={`${fieldName}.minDailyLockProfitSameKind`}
        value={formValues[fieldName]?.minDailyLockProfitSameKind?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداقل بازده قفل روزانه (خنثی-غیر همجنس)"
        name={`${fieldName}.minDailyLockProfitDifferentKind`}
        value={formValues[
          fieldName
        ]?.minDailyLockProfitDifferentKind?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداقل بازده قفل روزانه (خنثی-همجنس-غیر هم سررسید)"
        name={`${fieldName}.minDailyLockProfitDifferentApplyDay`}
        value={formValues[
          fieldName
        ]?.minDailyLockProfitDifferentApplyDay?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداقل بازده قفل روزانه (تضعیف)"
        name={`${fieldName}.minDailyLockProfitDifferentWeak`}
        value={formValues[
          fieldName
        ]?.minDailyLockProfitDifferentWeak?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="مرز حباب نرمال نسبی"
        name={`${fieldName}.relativeNormalBubbleTreshold`}
        value={formValues[fieldName]?.relativeNormalBubbleTreshold?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداقل قدرمطلق اختلاف حباب"
        name={`${fieldName}.minAbsDiffrential`}
        value={formValues[fieldName]?.minAbsDiffrential?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداقل قدرمطلق دلتا"
        name={`${fieldName}.minAbsDelta`}
        value={formValues[fieldName]?.minAbsDelta?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداقل حجم روز معاملاتی قبل"
        name={`${fieldName}.minLastTrade`}
        value={formValues[fieldName]?.minLastTrade?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداقل روز تا سررسید"
        name={`${fieldName}.minRemainingDay`}
        value={formValues[fieldName]?.minRemainingDay?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداکثر روز تا سررسید"
        name={`${fieldName}.maxRemainingDay`}
        value={formValues[fieldName]?.maxRemainingDay?.toString()}
        onChange={changeHandler}
        extraLabel
      />
    </div>
  );

  return (
    <div className="flex flex-col justify-start items-start gap-5">
      <div className="flex flex-row justify-start items-center gap-5">
        {loading ? <FullScreenLoading /> : <></>}
        <div className="w-[200px]">
          <TextInput
            name="goalDeltaTolerance"
            value={formValues.goalDeltaTolerance?.toString()}
            label="تلورانس دلتای هدف"
            onChange={commonChangeHandler}
            extraLabel
          />
        </div>
        <div className="w-[200px]">
          <TextInput
            name="goalGammaTolerance"
            value={formValues.goalGammaTolerance?.toString()}
            label="تلورانس گاما هدف"
            onChange={commonChangeHandler}
            extraLabel
          />
        </div>
      </div>
      {getForm("افزایش دلتا و گاما", "incDeltaIncGamma")}
      {getForm("کاهش دلتا و گاما", "decDeltaDecGamma")}
      {getForm("کاهش دلتا و افزایش گاما", "decDeltaIncGamma")}
      {getForm("افزایش دلتا و کاهش گاما", "incDeltaDecGamma")}
      <div className="w-24 mr-auto">
        <Button text="ثبت" onClick={submitHandler} variant="contained" />
      </div>
    </div>
  );
};

export default CentralConfig;
